// Per-browser alarm state (architecture sprint-14 §8.2, §8.4, §8.5): the ack, the ring instants, the SOP
// checklist ack and the alerts already notified. localStorage wrapped in try/catch with an in-memory fallback,
// so a blocked storage still works for this tab.
import { mergeAck, type AlarmAck, type RangMap } from './alarm-model';

export const ALARM_ACK_KEY = 'tt-alarm-ack';
export const ALARM_RANG_KEY = 'tt-alarm-rang';
export const ALERT_SEEN_KEY = 'tt-alert-seen';
export const SOP_ACK_PREFIX = 'tt-sop-ack:';
const ALERT_SEEN_MAX = 200;

const memory = new Map<string, string>();

function read(key: string): string | null {
  try {
    const v = window.localStorage.getItem(key);
    if (v !== null) return v;
  } catch {
    // fall through to memory
  }
  return memory.get(key) ?? null;
}

function write(key: string, value: string): void {
  memory.set(key, value);
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Storage blocked: the in-memory copy covers this tab.
  }
}

function parse<T>(raw: string | null, guard: (v: unknown) => v is T): T | null {
  if (raw === null) return null;
  try {
    const v: unknown = JSON.parse(raw);
    return guard(v) ? v : null;
  } catch {
    return null;
  }
}

function isAck(v: unknown): v is AlarmAck {
  const o = v as Partial<AlarmAck> | null;
  return typeof o === 'object' && o !== null && typeof o.shiftId === 'string' && typeof o.upTo === 'string' && typeof o.sent === 'boolean';
}

function isRang(v: unknown): v is { shiftId: string; rang: RangMap } {
  const o = v as { shiftId?: unknown; rang?: unknown } | null;
  return typeof o === 'object' && o !== null && typeof o.shiftId === 'string' && typeof o.rang === 'object' && o.rang !== null;
}

export function readAck(): AlarmAck | null {
  return parse(read(ALARM_ACK_KEY), isAck);
}

/** Merges with what is stored (another tab may be ahead) and returns the result. */
export function storeAck(ack: AlarmAck): AlarmAck {
  const merged = mergeAck(readAck(), ack);
  write(ALARM_ACK_KEY, JSON.stringify(merged));
  return merged;
}

export function readRang(shiftId: string): RangMap {
  const v = parse(read(ALARM_RANG_KEY), isRang);
  return v && v.shiftId === shiftId ? v.rang : {};
}

export function storeRang(shiftId: string, rang: RangMap): void {
  write(ALARM_RANG_KEY, JSON.stringify({ shiftId, rang }));
}

export function sopAcknowledged(userId: string): boolean {
  return read(`${SOP_ACK_PREFIX}${userId}`) !== null;
}

export function acknowledgeSop(userId: string): void {
  write(`${SOP_ACK_PREFIX}${userId}`, '1');
}

export function readSeenAlerts(): string[] {
  const v = parse(read(ALERT_SEEN_KEY), (x): x is string[] => Array.isArray(x) && x.every((i) => typeof i === 'string'));
  return v ?? [];
}

/** FIFO, at most 200 ids. */
export function addSeenAlerts(ids: readonly string[]): void {
  const next = [...readSeenAlerts(), ...ids.filter((i) => !readSeenAlerts().includes(i))].slice(-ALERT_SEEN_MAX);
  write(ALERT_SEEN_KEY, JSON.stringify(next));
}

// Sprint 15 (§9.2): review notifications already raised (≤ 200 event ids, FIFO).
export const NOTES_REVIEW_SEEN_KEY = 'tt-notes-review-seen';

export function readSeenNoteReviews(): string[] {
  return parse(read(NOTES_REVIEW_SEEN_KEY), (x): x is string[] => Array.isArray(x) && x.every((i) => typeof i === 'string')) ?? [];
}

export function addSeenNoteReviews(ids: readonly string[]): void {
  const seen = readSeenNoteReviews();
  write(NOTES_REVIEW_SEEN_KEY, JSON.stringify([...seen, ...ids.filter((i) => !seen.includes(i))].slice(-ALERT_SEEN_MAX)));
}

/** Test seam. */
export function resetAlarmStoreForTests(): void {
  memory.clear();
}
